import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { PencilIcon, Eye, EyeOff } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import apiClientChoice from "../../../utils/apiClientChoice";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

const accountStatusOptions = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

export function EditNubraUserModal({ userDetails, fetchFyersUserList, forceOpen = false, onForceClose, isPolarisOrPolarisLite: propIsPolaris }) {
  const [open, setOpen] = useState(false);
  const [showCustomLabelInput, setShowCustomLabelInput] = useState(false);

  // Determine polaris mode: from prop or from userDetails fields
  const isPolarisOrPolarisLite = propIsPolaris ?? (userDetails?.product_is_polaris || userDetails?.product_is_polaris_lite) ?? false;

  // ── Standard mode state ────────────────────────────────────────────────────
  const [userData, setUserData] = useState({
    clientId: "",
    name: "",
    email: "",
    totpKey: "",
    pin: "",
    phoneNumber: "",
    status: "",
    label: "",
    product: "optimus",
    proxyUser: "",
    proxyPassword: "",
    proxyHost: "",
    proxyPort: 0,
    proxyToggle: false,
    proxyStaticIp: "",
  });

  // ── Polaris mode state ─────────────────────────────────────────────────────
  const [polarisData, setPolarisData] = useState({
    client_id: "",
    name: "",
    email: "",
    password: "",
    phone: "",
    totp_key: "",
    dob: "",
    sizing: 0,
    account_status: "INACTIVE",
    proxy_user: "",
    proxy_password: "",
    proxy_host: "",
    proxy_port: 0,
    proxy_static_ip: "",
    proxy_toggle: false,
  });

  const [visibleFields, setVisibleFields] = useState({});
  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // ── Fetch standard user details ────────────────────────────────────────────
  const getDecryptedUserDetails = async () => {
    const data = { client_id: userDetails.client_id };
    const config = {
      method: "post",
      url: `${server.godseye.baseUrl}get-nubra-user-by-id`,
      headers: { "Content-Type": "application/json" },
      data: JSON.stringify(data),
    };
    try {
      const response = await apiClient.request(config);
      setUserData({
        clientId: response.data.client_id || "",
        name: response.data.name || "",
        email: response.data.email || "",
        totpKey: response.data.totp_key || "",
        pin: response.data.pin || "",
        phoneNumber: response.data.phone || "",
        status: response.data.status || "",
        label: response.data.label || "",
        product: response.data.product || "optimus",
        proxyUser: response.data.proxy_user || "",
        proxyPassword: response.data.proxy_password || "",
        proxyHost: response.data.proxy_host || "",
        proxyPort: response.data.proxy_port || 0,
        proxyToggle: response.data.proxy_toggle || false,
        proxyStaticIp: response.data.proxy_static_ip || "",
      });
      const predefinedLabels = ["clearmind", "satyam"];
      setShowCustomLabelInput(!predefinedLabels.includes(response.data.label || "") && response.data.label !== "");
    } catch (error) {
      console.error("Failed to get user details:", error);
    }
  };

  // ── Fetch polaris user details from Choice server ──────────────────────────
  const getPolarisUserDetails = async () => {
    try {
      const response = await apiClientChoice.get("https://choice.server.sypto.xyz/users");
      const allUsers = response.data || [];
      const user = allUsers.find(
        (u) => u.client_id === userDetails.client_id && u.broker === "NUBRA"
      );
      if (user) {
        setPolarisData({
          client_id: user.client_id || "",
          name: user.name || "",
          email: user.email || "",
          password: user.password || "",
          phone: user.phone || "",
          totp_key: user.totp_key || "",
          dob: user.dob || "",
          sizing: user.sizing ?? 0,
          account_status: user.account_status || "INACTIVE",
          proxy_user: user.proxy_user || "",
          proxy_password: user.proxy_password || "",
          proxy_host: user.proxy_host || user.proxy_host_db || "",
          proxy_port: user.proxy_port || 0,
          proxy_static_ip: user.proxy_static_ip || "",
          proxy_toggle: user.proxy_toggle || false,
        });
      } else {
        toast.error("User not found on Choice server");
      }
    } catch (error) {
      console.error("Failed to fetch user from Choice server:", error);
      toast.error("Failed to load user details");
    }
  };

  // ── Common handleChange ────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePolarisChange = (e) => {
    const { name, value } = e.target;
    setPolarisData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Standard update ────────────────────────────────────────────────────────
  const handleUpdateUser = () => {
    const data = {
      client_id: userData.clientId,
      name: userData.name,
      email: userData.email,
      totp_key: userData.totpKey,
      pin: userData.pin,
      status: userData.status,
      phone_number: userData.phoneNumber,
      label: userData.label,
      product: userData.product || "optimus",
      proxy_user: userData.proxyUser,
      proxy_password: userData.proxyPassword,
      proxy_host: userData.proxyHost,
      proxy_port: parseInt(userData.proxyPort),
      proxy_toggle: userData.proxyToggle,
      proxy_static_ip: userData.proxyStaticIp || null,
    };

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}edit-nubra-user`,
      headers: { "Content-Type": "application/json" },
      data: JSON.stringify(data),
    };

    apiClient
      .request(config)
      .then(() => {
        toast.success("Nubra user updated successfully!", { duration: 5000 });
      })
      .catch((error) => {
        console.error("Failed to update user", error);
        toast.error("Failed to update user", { duration: 5000 });
      })
      .finally(() => {
        setOpen(false);
        fetchFyersUserList();
      });
  };

  // ── Polaris update (Choice server) ────────────────────────────────────────
  const handleUpdatePolarisUser = async () => {
    const payload = {
      name: polarisData.name,
      email: polarisData.email,
      password: polarisData.password,
      phone: polarisData.phone,
      client_id: polarisData.client_id,
      active: polarisData.account_status === "ACTIVE",
      broker: "NUBRA",
      multiplier: "0",
      dob: polarisData.dob,
      totp_key: polarisData.totp_key,
      proxy_user: polarisData.proxy_user,
      proxy_password: polarisData.proxy_password,
      proxy_host_db: polarisData.proxy_host,
      proxy_port: parseInt(polarisData.proxy_port) || 0,
      proxy_static_ip: polarisData.proxy_static_ip,
      proxy_toggle: polarisData.proxy_toggle,
      type: "",
      session_id: "",
      pin: "",                       // empty for Nubra
      vendor_id: polarisData.client_id,
      sizing: parseInt(polarisData.sizing) || 0,
      account_status: polarisData.account_status,
    };

    try {
      await apiClientChoice.request({
        method: "put",
        url: `https://choice.server.sypto.xyz/update/user/${polarisData.client_id}`,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(payload),
      });
      toast.success("Nubra (Polaris) user updated successfully!", { duration: 5000 });
    } catch (error) {
      console.error("Failed to update Polaris Nubra user:", error);
      toast.error("Failed to update user", { duration: 5000 });
    } finally {
      setOpen(false);
      if (fetchFyersUserList) fetchFyersUserList();
    }
  };

  const handleOpenModal = () => {
    setOpen(true);
    if (isPolarisOrPolarisLite) {
      getPolarisUserDetails();
    } else {
      getDecryptedUserDetails();
    }
  };

  useEffect(() => {
    if (forceOpen) {
      handleOpenModal();
    }
  }, [forceOpen]);

  return (
    <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val && onForceClose) onForceClose(); }}>
      {!forceOpen && (
        <DialogTrigger asChild>
          <Button
            onClick={handleOpenModal}
            variant="outline"
            className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
          >
            <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
            Edit User
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-[1025px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit Nubra User Details
            {isPolarisOrPolarisLite && (
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-purple-600/20 text-purple-400 border border-purple-500">
                Polaris / Choice Server
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {isPolarisOrPolarisLite ? (
          /* ── Polaris form ───────────────────────────────────────────────── */
          <div className="grid grid-cols-3 gap-4 py-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4">
            {[
              { label: "Client ID", field: "client_id", readOnly: true },
              { label: "Name", field: "name" },
              { label: "Email", field: "email" },
              { label: "Phone", field: "phone" },
              { label: "Password", field: "password", sensitive: true },
              { label: "TOTP Key", field: "totp_key", sensitive: true },
              { label: "Date of Birth", field: "dob" },
              { label: "Sizing", field: "sizing", type: "number" },
              { label: "Proxy User", field: "proxy_user" },
              { label: "Proxy Password", field: "proxy_password", sensitive: true },
              { label: "Proxy Host", field: "proxy_host" },
              { label: "Proxy Port", field: "proxy_port", type: "number" },
              { label: "Proxy Static IP", field: "proxy_static_ip" },
            ].map(({ label, field, sensitive, readOnly, type }) => (
              <div key={field} className="grid grid-cols-1 items-center gap-4">
                <Label className="text-white">{label}</Label>
                <div className="relative">
                  <Input
                    className="text-white pr-10"
                    type={sensitive && !visibleFields[field] ? "password" : (type || "text")}
                    name={field}
                    value={polarisData[field]}
                    readOnly={readOnly}
                    onChange={handlePolarisChange}
                  />
                  {sensitive && (
                    <button
                      type="button"
                      onClick={() => toggleVisibility(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      {visibleFields[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Account Status dropdown */}
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Account Status</Label>
              <Select
                value={polarisData.account_status}
                onValueChange={(val) => setPolarisData((prev) => ({ ...prev, account_status: val }))}
              >
                <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent className="border-zinc-700">
                  <SelectGroup>
                    {accountStatusOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Proxy Toggle */}
            <div className="flex items-center space-x-2 h-full pt-8">
              <Checkbox
                id="proxy-toggle-polaris-edit-nubra"
                checked={polarisData.proxy_toggle}
                onCheckedChange={(checked) => setPolarisData((prev) => ({ ...prev, proxy_toggle: checked }))}
              />
              <Label htmlFor="proxy-toggle-polaris-edit-nubra" className="text-white cursor-pointer">
                Proxy Toggle
              </Label>
            </div>
          </div>
        ) : (
          /* ── Standard form ──────────────────────────────────────────────── */
          <div className="grid grid-cols-3 gap-4 py-4">
            {Object.keys(userData)
              .filter((field) => !["status", "label", "proxyToggle", "proxyStaticIp"].includes(field))
              .map((field) => (
                <div key={field} className="grid grid-cols-1 items-center gap-4">
                  <Label className="text-white">
                    {field.replace(/([A-Z])/g, " $1")}
                  </Label>
                  <div className="relative">
                    <Input
                      className="text-white pr-10"
                      type={
                        (field.toLowerCase().includes("password") ||
                          field.toLowerCase().includes("secret") ||
                          field.toLowerCase().includes("key") ||
                          field.toLowerCase().includes("pin") ||
                          field.toLowerCase().includes("token")) && !visibleFields[field]
                          ? "password"
                          : "text"
                      }
                      placeholder={field.replace(/([A-Z])/g, " $1")}
                      name={field}
                      value={userData[field]}
                      onChange={handleChange}
                    />
                    {(field.toLowerCase().includes("password") ||
                      field.toLowerCase().includes("secret") ||
                      field.toLowerCase().includes("key") ||
                      field.toLowerCase().includes("pin") ||
                      field.toLowerCase().includes("token")) && (
                      <button
                        type="button"
                        onClick={() => toggleVisibility(field)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                      >
                        {visibleFields[field] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}

            {/* Status dropdown */}
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Status</Label>
              <Select
                value={userData.status}
                onValueChange={(val) => setUserData((prev) => ({ ...prev, status: val }))}
              >
                <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent className="border-zinc-700">
                  <SelectGroup>
                    {statusOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Product dropdown */}
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Product</Label>
              <Select
                value={userData.product || "optimus"}
                onValueChange={(val) => setUserData((prev) => ({ ...prev, product: val }))}
              >
                <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                  <SelectValue placeholder="Select Product" />
                </SelectTrigger>
                <SelectContent className="border-zinc-700">
                  <SelectGroup>
                    <SelectItem value="optimus">Optimus</SelectItem>
                    <SelectItem value="multi_asset">Multi-Asset</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Label dropdown */}
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Label</Label>
              <Select
                value={showCustomLabelInput ? "custom" : userData.label}
                onValueChange={(val) => {
                  if (val === "custom") {
                    setShowCustomLabelInput(true);
                  } else {
                    setShowCustomLabelInput(false);
                    setUserData((prev) => ({ ...prev, label: val }));
                  }
                }}
              >
                <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                  <SelectValue placeholder="Select Label" />
                </SelectTrigger>
                <SelectContent className="border-zinc-700">
                  <SelectGroup>
                    <SelectItem value="clearmind">clearmind</SelectItem>
                    <SelectItem value="satyam">satyam</SelectItem>
                    <SelectItem value="custom">Custom (Type your own)</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {showCustomLabelInput && (
              <div className="grid grid-cols-1 items-center gap-4">
                <Label className="text-white">Custom Label</Label>
                <Input
                  className="text-white mt-2"
                  type="text"
                  placeholder="Enter custom label"
                  name="label"
                  value={userData.label}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Proxy Static IP */}
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Proxy Static IP</Label>
              <Input
                className="text-white"
                type="text"
                placeholder="e.g. 2402:1f00:8300:989::1"
                name="proxyStaticIp"
                value={userData.proxyStaticIp}
                onChange={handleChange}
              />
            </div>

            <div className="flex items-center space-x-2 h-full pt-8">
              <Checkbox
                id="proxy-toggle"
                checked={userData.proxyToggle}
                onCheckedChange={(checked) =>
                  setUserData((prev) => ({ ...prev, proxyToggle: checked }))
                }
              />
              <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
                Proxy Toggle
              </Label>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6 flex justify-between">
          <Button
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={isPolarisOrPolarisLite ? handleUpdatePolarisUser : handleUpdateUser}
          >
            {isPolarisOrPolarisLite ? "Update Nubra (Polaris) User" : "Update User Details"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
